import { analyticsConfig, AnalyticsEvent, ErrorEvent, SearchEvent, PlaylistEvent, ShareEvent } from '../../config/analytics.config';

interface SupabaseClient {
  from: (table: string) => {
    insert: (data: any) => Promise<{ data: any; error: any }>;
    select: (query?: string) => {
      gte: (column: string, value: any) => Promise<{ data: any; error: any }>;
    };
  };
}

// Event queue for batching
interface QueuedEvent {
  table: string;
  data: any;
  timestamp: number;
}

export class SupabaseAnalyticsService {
  private static instance: SupabaseAnalyticsService;
  private client: SupabaseClient | null = null;
  private eventQueue: QueuedEvent[] = [];
  private batchTimer: NodeJS.Timeout | null = null;
  private sessionId: string;
  private readonly BATCH_SIZE = 50;
  private readonly BATCH_INTERVAL = 5000; // 5 seconds

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeSupabase();
    
    // Listen for page unload to flush events
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
    }
  }

  static getInstance(): SupabaseAnalyticsService {
    if (!SupabaseAnalyticsService.instance) {
      SupabaseAnalyticsService.instance = new SupabaseAnalyticsService();
    }
    return SupabaseAnalyticsService.instance;
  }

  private async initializeSupabase(): Promise<void> {
    if (!analyticsConfig.supabase.enabled || !analyticsConfig.supabase.url || !analyticsConfig.supabase.anonKey) {
      console.log('Supabase analytics disabled or not configured');
      return;
    }

    try {
      // Dynamically import Supabase client
      const { createClient } = await import('@supabase/supabase-js');
      this.client = createClient(
        analyticsConfig.supabase.url,
        analyticsConfig.supabase.anonKey
      ) as unknown as SupabaseClient;
    } catch (error) {
      console.error('Failed to initialize Supabase:', error);
      // Fall back to localStorage if Supabase fails
      this.fallbackToLocalStorage();
    }
  }

  // Queue an event for batching
  private queueEvent(table: string, data: any): void {
    const eventData: any = {
      ...data,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent
    };
    
    // Add screen resolution for analytics_events table
    if (table === 'analytics_events') {
      eventData.screen_resolution = `${window.screen.width}x${window.screen.height}`;
      eventData.page_path = window.location.pathname;
      eventData.page_title = document.title;
      eventData.referrer = document.referrer;
      eventData.platform = navigator.platform;
    }
    
    this.eventQueue.push({
      table,
      data: eventData,
      timestamp: Date.now()
    });

    // Start batch timer if not already running
    if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => this.processBatch(), this.BATCH_INTERVAL);
    }

    // Process immediately if batch size reached
    if (this.eventQueue.length >= this.BATCH_SIZE) {
      this.processBatch();
    }
  }

  // Process batched events
  private async processBatch(): Promise<void> {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.eventQueue.length === 0) return;

    const eventsToProcess = [...this.eventQueue];
    this.eventQueue = [];

    if (!this.client) {
      // Store in localStorage if Supabase not available
      this.storeInLocalStorage(eventsToProcess);
      return;
    }

    // Group events by table
    const eventsByTable = eventsToProcess.reduce((acc, event) => {
      if (!acc[event.table]) acc[event.table] = [];
      acc[event.table].push(event.data);
      return acc;
    }, {} as Record<string, any[]>);

    // Insert events by table
    for (const [table, events] of Object.entries(eventsByTable)) {
      try {
        const { error } = await this.client.from(table).insert(events);
        if (error) {
          console.error(`Failed to insert ${table} events:`, error);
          this.storeInLocalStorage(eventsToProcess.filter(e => e.table === table));
        }
      } catch (error) {
        console.error(`Error inserting ${table} events:`, error);
        this.storeInLocalStorage(eventsToProcess.filter(e => e.table === table));
      }
    }
  }

  // Track generic event
  trackEvent(event: AnalyticsEvent): void {
    this.queueEvent('analytics_events', {
      event_name: event.name,
      event_category: event.parameters?.category,
      event_label: event.parameters?.label,
      event_value: event.parameters?.value,
      metadata: event.parameters,
      user_id: event.userId || this.sessionId
    });
  }

  // Track search
  trackSearch(searchEvent: SearchEvent): void {
    this.queueEvent('analytics_events', {
      event_name: 'search_artist',
      event_category: 'search',
      event_label: searchEvent.artist || searchEvent.query,
      event_value: searchEvent.resultsCount,
      metadata: {
        query: searchEvent.query,
        artist: searchEvent.artist,
        results_count: searchEvent.resultsCount,
        search_type: searchEvent.searchType,
        time_to_results: searchEvent.timeToResults
      },
      user_id: this.sessionId
    });
  }

  // Track playlist
  trackPlaylist(playlistEvent: PlaylistEvent): void {
    this.queueEvent('analytics_events', {
      event_name: `playlist_${playlistEvent.action}`,
      event_category: 'playlist',
      event_label: playlistEvent.artistName,
      event_value: playlistEvent.songCount,
      metadata: {
        action: playlistEvent.action,
        artist_name: playlistEvent.artistName,
        artist_id: playlistEvent.artistId,
        song_count: playlistEvent.songCount,
        platform: playlistEvent.platform,
        playlist_id: playlistEvent.playlistId
      },
      user_id: this.sessionId
    });
  }

  // Track share
  trackShare(shareEvent: ShareEvent): void {
    this.queueEvent('analytics_events', {
      event_name: `share_${shareEvent.method}`,
      event_category: 'share',
      event_label: shareEvent.artistName,
      event_value: shareEvent.success ? 1 : 0,
      metadata: {
        method: shareEvent.method,
        artist_name: shareEvent.artistName,
        playlist_id: shareEvent.playlistId,
        success: shareEvent.success
      },
      user_id: this.sessionId
    });
  }

  // Track error
  trackError(errorEvent: ErrorEvent): void {
    this.queueEvent('error_events', {
      error_type: errorEvent.error.name,
      error_message: errorEvent.error.message,
      error_stack: errorEvent.error.stack,
      severity: errorEvent.severity,
      context: errorEvent.context,
      page_path: window.location.pathname,
      user_id: this.sessionId
    });
  }

  // Flush all pending events
  async flush(): Promise<void> {
    if (this.eventQueue.length > 0) {
      await this.processBatch();
    }
  }

  // Fallback to localStorage
  private fallbackToLocalStorage(): void {
    console.log('Using localStorage fallback for analytics');
  }

  // Store events in localStorage
  private storeInLocalStorage(events: QueuedEvent[]): void {
    try {
      const stored = localStorage.getItem('festprep_analytics_queue') || '[]';
      const existing = JSON.parse(stored);
      const updated = [...existing, ...events].slice(-1000); // Keep last 1000 events
      localStorage.setItem('festprep_analytics_queue', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to store analytics in localStorage:', error);
    }
  }

  // Retry failed events from localStorage
  async retryFailedEvents(): Promise<void> {
    if (!this.client) return;

    try {
      const stored = localStorage.getItem('festprep_analytics_queue');
      if (!stored) return;

      const events = JSON.parse(stored) as QueuedEvent[];
      if (events.length === 0) return;

      // Clear localStorage first
      localStorage.removeItem('festprep_analytics_queue');

      // Re-queue events
      events.forEach(event => {
        this.eventQueue.push(event);
      });

      await this.processBatch();
    } catch (error) {
      console.error('Failed to retry analytics events:', error);
    }
  }

  // Get browser info
  private getBrowserInfo(): Record<string, any> {
    const ua = navigator.userAgent;
    return {
      user_agent: ua,
      language: navigator.language,
      platform: navigator.platform,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }

  // Generate session ID
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get dashboard stats (for internal use)
  async getDashboardStats(): Promise<any> {
    if (!this.client) return null;

    try {
      // You can implement custom queries here
      const { data: todayStats } = await this.client
        .from('analytics_events')
        .select('*')
        .gte('timestamp', new Date().toISOString().split('T')[0]);

      return {
        eventsToday: todayStats?.length || 0,
        // Add more stats as needed
      };
    } catch (error) {
      console.error('Failed to get dashboard stats:', error);
      return null;
    }
  }
}

// Export singleton instance
export const supabaseAnalytics = SupabaseAnalyticsService.getInstance();